
interface CategoryData {
    dataArr: any[];
    requestsCount: number;
    matchesCount: number;
    total: number;
    title: string;
    inboxCount?: number;
  }

export type Perspectives ={
    label: string;          // Dynamic label for each perspective
    key: string;            // Key or fieldPath for fetching the category value
    fieldPath?: string;     // Optionally, a path to the field if nested
    countType: 'requests' | 'matches' | 'inbox'; // Type of count to associate with this perspective
  }[];



export const getCalculatedPerspectivesAi = async (
  allRequests: any[],
  perspectiveConfigurations: Perspectives
): Promise<Record<string, CategoryData>> => {
  
  // Initialize an object to hold dynamic sets for each perspective category
  const categorySets = perspectiveConfigurations.reduce((acc, perspective) => {
    acc[perspective.key] = new Set<string>();
    return acc;
  }, {} as Record<string, Set<string>>);
  
  // Populate categories dynamically from all requests based on the perspectives
  allRequests.forEach((request: any) => {
    perspectiveConfigurations.forEach((perspective) => {
      let value;
      if (perspective.fieldPath) {
        // Resolve nested fields like request.company.tier
        value = perspective.fieldPath.split('.').reduce((acc, key) => acc && acc[key], request);
      } else {
        value = request[perspective.key]; // Direct key
      }
  
      if (value) {
        categorySets[perspective.key].add(value);
      }
    });
  });
  
  // Initialize category arrays dynamically based on the perspectives
  const initializeCategoryArray = (categorySet: Set<string>, perspective: typeof perspectiveConfigurations[0]) => {
    return Array.from(categorySet, value => ({
      [perspective.key]: value,
      value: value,
      matchesCount: 0,
      total: 0,
    }));
  };
  
  // Generate arrays for each perspective category
  const categoryData = perspectiveConfigurations.reduce((acc, perspective) => {
    acc[perspective.key] = initializeCategoryArray(categorySets[perspective.key], perspective);
    return acc;
  }, {} as Record<string, any[]>);
  
  // Count requests, matches, and inbox for each category dynamically
  allRequests.forEach((request: any) => {
    perspectiveConfigurations.forEach((perspective) => {
      // console.log({perspective, request})
      let value: any;
      if (perspective.fieldPath) {
        value = perspective.fieldPath.split('.').reduce((acc, key) => acc && acc[key], request);
      } else {
        value = request[perspective.key];
      }
  
      if (value) {
        const arr = categoryData[perspective.key];
          
        const item = arr.find((item: any) => item[perspective.key]?.toLowerCase() === value?.toLowerCase());
  
        if (item) {
           
          item.matchesCount++;
          item.total++;
        }
      }
    });
  });
  
  // Adjust percentages and total dynamically
  let maxTotal = 0;
    
  Object.keys(categoryData).forEach(key => {
    const arr = categoryData[key];
      
    arr.forEach(item => {
      
      if (item.matchesCount  > maxTotal) {
        maxTotal = item.matchesCount;
      }
    });
  });
  
  // Adjust percentages dynamically and sort
  Object.keys(categoryData).forEach(key => {
    const arr = categoryData[key];
      
    arr.forEach(item => {
      if (maxTotal > 0) {
        item.matchesPercentage = (item.matchesCount / maxTotal) * 100;
        item.bgPercentage = 100  - item.matchesPercentage
          
      }  else {
        item.matchesPercentage = 0;
        item.bgPercentage = 100;
      } 
    });
  
    arr.sort((a, b) => a.bgPercentage - b.bgPercentage);
  
  });
  
  // Return structured data for each perspective category
  const result = perspectiveConfigurations.reduce((acc, perspective) => {
    const dataArr = categoryData[perspective.key];
    acc[perspective.key] = {
      dataArr,
      requestsCount: dataArr.reduce((sum, curr) => sum + curr.requestsCount, 0),
      matchesCount: dataArr.reduce((sum, curr) => sum + curr.matchesCount, 0),
      total: (dataArr.reduce((sum, curr) => sum + curr.requestsCount, 0) || 0)
      + (dataArr.reduce((sum, curr) => sum + curr.matchesCount, 0) || 0) 
      ,
      title: perspective.label || perspective.key
    };
    return acc;
  }, {} as Record<string, CategoryData>);
  
  return result;
};